/**
 * Stone Investment Contact Form Section
 *
 * Stone-themed wrapper for the core ContactFormSection component.
 * Preserves Stone's luxury aesthetic: square corners, underlined-style inputs,
 * Cormorant Garamond headings, Montserrat body, greyscale palette.
 */

'use client';

import { memo } from 'react';
import { ContactFormSection, type ContactFormField } from '@/components/contact/ContactFormSection';

const safeGetString = (value: unknown, fallback = ''): string => {
  if (typeof value === 'string') return value;
  return fallback;
};

const safeGetObject = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>;
  return {};
};

export interface StoneContactFormSectionProps {
  title?: string;
  subtitle?: string;
  propertyName?: string;
  onSubmit?: (data: ContactFormData) => Promise<void>;
  messages?: Record<string, unknown>;
  className?: string;
}

export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  propertyInterest?: string;
}

export const StoneContactFormSection = memo(function StoneContactFormSection({
  title,
  subtitle,
  propertyName,
  onSubmit,
  messages,
  className = '',
}: StoneContactFormSectionProps) {
  const stoneMessages = safeGetObject(messages?.stone);
  const formMessages = safeGetObject(stoneMessages.contactForm);

  const subjectOptions = [
    { value: 'property-inquiry', label: safeGetString(formMessages.subjectProperty, 'Property Inquiry') },
    { value: 'valuation', label: safeGetString(formMessages.subjectValuation, 'Property Valuation') },
    { value: 'selling', label: safeGetString(formMessages.subjectSelling, 'Selling a Property') },
    { value: 'general', label: safeGetString(formMessages.subjectGeneral, 'General Inquiry') },
  ];

  const fields: ContactFormField[] = [
    {
      name: 'name',
      label: safeGetString(formMessages.nameLabel, 'Your Name'),
      placeholder: safeGetString(formMessages.namePlaceholder, 'John Smith'),
      type: 'text',
      required: true,
    },
    {
      name: 'email',
      label: safeGetString(formMessages.emailLabel, 'Email Address'),
      placeholder: safeGetString(formMessages.emailPlaceholder, 'john@example.com'),
      type: 'email',
      required: true,
    },
    {
      name: 'phone',
      label: safeGetString(formMessages.phoneLabel, 'Phone Number'),
      placeholder: safeGetString(formMessages.phonePlaceholder, '+33 6 00 00 00 00'),
      type: 'tel',
      required: false,
    },
    {
      name: 'subject',
      label: safeGetString(formMessages.subjectLabel, 'Subject'),
      placeholder: safeGetString(formMessages.subjectPlaceholder, 'Select a subject'),
      type: 'select',
      required: false,
      options: subjectOptions,
    },
    {
      name: 'message',
      label: safeGetString(formMessages.messageLabel, 'Your Message'),
      placeholder: safeGetString(formMessages.messagePlaceholder, 'Tell us about your requirements...'),
      type: 'textarea',
      required: true,
    },
  ];

  if (propertyName) {
    const propertyField: ContactFormField = {
      name: 'propertyInterest',
      label: safeGetString(formMessages.propertyLabel, 'Property of Interest'),
      placeholder: propertyName,
      type: 'text',
      required: false,
    };
    fields.splice(3, 0, propertyField);
  }

  const resolvedTitle = title || safeGetString(formMessages.title, 'Get in Touch');
  const resolvedSubtitle = subtitle || safeGetString(formMessages.subtitle, undefined);

  return (
    <section className={`py-20 lg:py-32 bg-white ${className}`}>
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Stone-styled header above the core form */}
        <div className="mb-12 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-sans mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {safeGetString(formMessages.eyebrow, 'Contact')}
          </p>
        </div>

        <ContactFormSection
          title={resolvedTitle}
          subtitle={resolvedSubtitle}
          fields={fields}
          submitLabel={safeGetString(formMessages.submit, 'Send Message')}
          sendingLabel={safeGetString(formMessages.sending, 'Sending...')}
          successMessage={safeGetString(formMessages.successMessage, 'Thank you for your inquiry. We will be in touch shortly.')}
          errorMessage={safeGetString(formMessages.submitError, 'Something went wrong. Please try again.')}
          {...(onSubmit ? { onSubmit: (data: Record<string, string>) => onSubmit(data as unknown as ContactFormData) } : {})}
          className="stone-contact-form-section"
          renderField={(field, value, onChange, error) => (
            <div className="mb-6">
              <label
                htmlFor={field.name}
                className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {field.label}
                {field.required && <span className="ml-1">*</span>}
              </label>

              {field.type === 'textarea' ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                  rows={6}
                  className={`
                    w-full px-4 py-3 border bg-white text-stone-900
                    placeholder:text-stone-400 focus:outline-none focus:border-stone-500
                    transition-colors resize-none
                    ${error ? 'border-red-500' : 'border-stone-300'}
                  `}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              ) : field.type === 'select' ? (
                <select
                  id={field.name}
                  name={field.name}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  required={field.required}
                  className={`
                    w-full px-4 py-3 border bg-white text-stone-900
                    focus:outline-none focus:border-stone-500 transition-colors
                    ${error ? 'border-red-500' : 'border-stone-300'}
                  `}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <option value="">{field.placeholder}</option>
                  {field.options?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  id={field.name}
                  name={field.name}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                  className={`
                    w-full px-4 py-3 border bg-white text-stone-900
                    placeholder:text-stone-400 focus:outline-none focus:border-stone-500
                    transition-colors
                    ${error ? 'border-red-500' : 'border-stone-300'}
                  `}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
              )}

              {error && (
                <p className="mt-1.5 text-sm text-red-600"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {error}
                </p>
              )}
            </div>
          )}
        />

        <style jsx global>{`
          .stone-contact-form-section .contact-form-header {
            text-align: center;
            margin-bottom: 3rem;
          }

          .stone-contact-form-section .contact-form-title {
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-size: 2.5rem;
            font-weight: 400;
            color: rgb(28 25 23);
            margin-bottom: 1rem;
          }

          .stone-contact-form-section .contact-form-subtitle {
            font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            font-size: 1rem;
            color: rgb(87 83 78);
            max-width: 32rem;
            margin: 0 auto;
          }

          .stone-contact-form-section .contact-form-submit {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
            padding: 1rem 2rem;
            background-color: rgb(28 25 23);
            color: white;
            font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            font-size: 0.875rem;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.15em;
            border: none;
            cursor: pointer;
            transition: background-color 0.3s ease;
          }

          .stone-contact-form-section .contact-form-submit:hover:not(:disabled) {
            background-color: rgb(41 37 36);
          }

          .stone-contact-form-section .contact-form-submit:disabled {
            background-color: rgb(168 162 158);
            cursor: not-allowed;
          }

          .stone-contact-form-section .contact-form-error-message {
            margin-top: 1rem;
            padding: 0.75rem 1rem;
            color: rgb(220 38 38);
            font-family: 'Montserrat', sans-serif;
            font-size: 0.875rem;
            text-align: center;
          }

          .stone-contact-form-section .success-animation-container {
            font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          .stone-contact-form-section .success-title {
            font-family: 'Cormorant Garamond', Georgia, serif !important;
            color: rgb(28 25 23) !important;
            font-weight: 400 !important;
            font-style: italic;
          }

          .stone-contact-form-section .success-message {
            font-family: 'Montserrat', sans-serif !important;
            color: rgb(87 83 78) !important;
          }

          .stone-contact-form-section .success-subtext {
            font-family: 'Montserrat', sans-serif !important;
          }

          .stone-contact-form-section .send-another-btn {
            font-family: 'Montserrat', sans-serif !important;
            border-radius: 0 !important;
            color: rgb(87 83 78) !important;
            border-color: rgb(214 211 209) !important;
          }

          .stone-contact-form-section .send-another-btn:hover {
            border-color: rgb(28 25 23) !important;
            color: rgb(28 25 23) !important;
          }

          .stone-contact-form-section .circle-progress {
            stroke: rgb(28 25 23) !important;
          }

          .stone-contact-form-section .checkmark-path {
            stroke: rgb(87 83 78) !important;
          }

          .stone-contact-form-section .pulse-ring {
            stroke: rgb(168 162 158) !important;
          }

          .stone-contact-form-section .sparkle {
            color: rgb(168 162 158) !important;
          }

          .stone-contact-form-section .confetti-piece {
            display: none !important;
          }

          @media (max-width: 640px) {
            .stone-contact-form-section .contact-form-title {
              font-size: 2rem;
            }
          }
        `}</style>
      </div>
    </section>
  );
});

export default StoneContactFormSection;
